// context/UserContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, query, where, doc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';

export interface UserData {
  id: string;
  nombre: string;
  correo: string;
  telefono?: string;
  rol: 'ADMIN' | 'ADMIN_COMITE' | 'ASOCIADO' | 'AFILIADO' | 'PUBLICO';
  tipo: string | string[];
  inmobiliaria_doc_id?: string; // ✅ Cambiado a doc.id
  inmobiliaria_nombre?: string;
  propiedad_doc_ids?: string[]; // ✅ Cambiado a doc.ids
  originalDoc: any;
  // Permisos de admin de comité
  esAdminComite?: boolean;
  modulosAcceso?: string[]; // ['talleres', 'eventos', etc.]
  adminComiteDocId?: string;
  [key: string]: any;
}

interface UserContextType {
  user: UserData | null;
  setUser: (user: UserData | null) => void;
  logout: () => Promise<void>;
  loading: boolean; // 👈 nuevo
  refreshUser: () => Promise<void>; // 👈 función para refrescar datos del usuario
}

const UserContext = createContext<UserContextType | undefined>(undefined);

function resolverRol({ adminDoc, asociadoDoc, asesorDoc }: {
  adminDoc?: any; asociadoDoc?: any; asesorDoc?: any;
}): { rol: 'ADMIN' | 'ADMIN_COMITE' | 'ASOCIADO' | 'AFILIADO' | 'PUBLICO', doc: any } {
  if (adminDoc && Array.isArray(adminDoc.tipo) && adminDoc.tipo.includes('admin')) {
    return { rol: 'ADMIN', doc: adminDoc };
  }
  // Admin de comité — coordinadores con acceso limitado a módulos específicos
  if (adminDoc && Array.isArray(adminDoc.tipo) && adminDoc.tipo.includes('admin_comite') && adminDoc.activo !== false) {
    return { rol: 'ADMIN_COMITE', doc: adminDoc };
  }
  if (asociadoDoc && asociadoDoc.tipo === 'asociado') {
    return { rol: 'ASOCIADO', doc: asociadoDoc };
  }
  if (asesorDoc && asesorDoc.tipo === 'asesor') {
    return { rol: 'AFILIADO', doc: asesorDoc };
  }
  return { rol: 'PUBLICO', doc: null };
}

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser debe usarse dentro de un UserProvider');
  return ctx;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionListenerRef = useRef<(() => void) | null>(null);
  // Sprint 5 (F2-003): flag para cancelar setup de listener si el usuario se deslogueó mientras cargaba
  const currentLoadRef = useRef<number>(0);
  // Sprint 5 (F2-004): refs para timer y referencia estable al user actual (evita rerun de useEffect por cambio user)
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userRef = useRef<UserData | null>(null);
  const logoutRef = useRef<((showMessage?: boolean) => Promise<void>) | null>(null);
  const navigate = useNavigate();

  // 🔐 Función para registrar sesión activa (solo para no-admins)
  const registrarSesion = async (uid: string, correo: string, rol: string) => {
    // Solo aplicar control de sesiones a Afiliados, Asociados y Admin de Comité (NO a ADMIN general)
    if (rol === 'ADMIN') {
      return null;
    }

    try {
      const nuevoSessionId = uuidv4();

      // Cerrar sesiones anteriores del mismo usuario
      const sesionesAnteriores = await getDocs(
        query(collection(db, 'sesiones_activas'), where('uid', '==', uid), where('activa', '==', true))
      );

      for (const sesionDoc of sesionesAnteriores.docs) {
        await updateDoc(sesionDoc.ref, { activa: false });
      }

      // Crear nueva sesión
      const docRef = doc(db, 'sesiones_activas', `${uid}_${nuevoSessionId}`);
      await setDoc(docRef, {
        uid,
        sessionId: nuevoSessionId,
        correo,
        createdAt: serverTimestamp(),
        lastActivity: serverTimestamp(),
        userAgent: navigator.userAgent,
        activa: true
      });

      // Guardar en sessionStorage para persistencia
      sessionStorage.setItem('ampi_session_id', nuevoSessionId);
      setSessionId(nuevoSessionId);

      return nuevoSessionId;
    } catch (error) {
      console.error('❌ Error al registrar sesión:', error);
      return null;
    }
  };

  // 🔐 Función para cerrar sesión en Firestore
  const cerrarSesionFirestore = async (uid: string, currentSessionId: string | null) => {
    if (!currentSessionId) return;

    try {
      const docRef = doc(db, 'sesiones_activas', `${uid}_${currentSessionId}`);
      await updateDoc(docRef, { activa: false });
    } catch (error) {
      console.error('Error al cerrar sesión en Firestore:', error);
    }
  };

  // 🔄 Función para cargar datos del usuario (extraída para reutilización)
  const loadUserData = async (firebaseUser: any) => {
    try {
      if (!firebaseUser) {
        setUser(null);
        return;
      }

      const uid = firebaseUser.uid;
      const correoAuth = (firebaseUser.email || '').toLowerCase();

      // 🔎 Buscamos por *_id == uid  y también por correo == email
      const [
        adminById, adminByEmail,
        asociadoById, asociadoByEmail,
        asesorById, asesorByEmail
      ] = await Promise.all([
        getDocs(query(collection(db, 'admin'), where('admin_id', '==', uid))),
        correoAuth ? getDocs(query(collection(db, 'admin'), where('correo', '==', correoAuth))) : Promise.resolve({ empty: true, docs: [] } as any),

        getDocs(query(collection(db, 'asociado'), where('asociado_id', '==', uid))),
        correoAuth ? getDocs(query(collection(db, 'asociado'), where('correo', '==', correoAuth))) : Promise.resolve({ empty: true, docs: [] } as any),

        getDocs(query(collection(db, 'asesor'), where('asesor_id', '==', uid))),
        correoAuth ? getDocs(query(collection(db, 'asesor'), where('correo', '==', correoAuth))) : Promise.resolve({ empty: true, docs: [] } as any),
      ]);

      const pick = (snapA: any, snapB: any) => {
        if (snapA && !snapA.empty) return { id: snapA.docs[0].id, ...snapA.docs[0].data() };
        if (snapB && !snapB.empty) return { id: snapB.docs[0].id, ...snapB.docs[0].data() };
        return null;
      };

      const adminDoc = pick(adminById, adminByEmail);
      const asociadoDoc = pick(asociadoById, asociadoByEmail);
      const asesorDoc = pick(asesorById, asesorByEmail);

      const { rol, doc: activeDoc } = resolverRol({ adminDoc, asociadoDoc, asesorDoc });

      if (rol === 'PUBLICO' || !activeDoc) {
        setUser(null);
        return;
      }

      // 🔐 Verificar si el usuario tiene permisos de admin de comité
      // (buscar en colección admin por correo con tipo admin_comite)
      let esAdminComite = false;
      let modulosAcceso: string[] = [];
      let adminComiteDocId = '';

      if (rol !== 'ADMIN') {
        // Solo buscar si NO es admin general (los admin generales ya tienen acceso total)
        try {
          const adminComiteQuery = query(
            collection(db, 'admin'),
            where('correo', '==', correoAuth),
            where('activo', '==', true)
          );
          const adminComiteSnap = await getDocs(adminComiteQuery);

          if (!adminComiteSnap.empty) {
            const adminComiteDoc = adminComiteSnap.docs[0];
            const adminComiteData = adminComiteDoc.data();

            // Verificar si tiene tipo admin_comite
            if (Array.isArray(adminComiteData.tipo) && adminComiteData.tipo.includes('admin_comite')) {
              esAdminComite = true;
              modulosAcceso = adminComiteData.modulos_acceso || ['talleres'];
              adminComiteDocId = adminComiteDoc.id;
              // permisos de comité detectados
            }
          }
        } catch (e) {
          console.error('Error al verificar permisos de comité:', e);
        }
      }

      // nombre de inmobiliaria (si aplica)
      let inmobiliariaNombre = '';
      const inmobiliariaDocId = activeDoc.inmobiliaria_doc_id;
      if (inmobiliariaDocId && (rol === 'ASOCIADO' || rol === 'AFILIADO')) {
        try {
          // ✅ Usar getDoc con doc.id directamente (más eficiente)
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            inmobiliariaNombre = (inmoSnap.data() as any).nombre || '';
          }
        } catch {}
      }

      const userData: UserData = {
        id: activeDoc.id,
        nombre: activeDoc.nombre || '',
        correo: (activeDoc.correo || '').toLowerCase(),
        telefono: activeDoc.telefono || '',
        ciudad: activeDoc.ciudad || activeDoc.direccion?.ciudad || 'Sin ciudad',
        rol,
        tipo: activeDoc.tipo,
        inmobiliaria_doc_id: inmobiliariaDocId || '', // ✅ Cambiado a doc.id
        inmobiliaria_nombre: inmobiliariaNombre,
        propiedad_doc_ids: activeDoc.propiedad_doc_ids || [], // ✅ Cambiado a doc.ids
        originalDoc: activeDoc,
        // Permisos de admin de comité
        esAdminComite,
        modulosAcceso,
        adminComiteDocId,
        ...(rol === 'ASOCIADO' && {
          anos_experiencia: activeDoc.anos_experiencia,
          biografia: activeDoc.biografia,
          calificacion: activeDoc.calificacion,
          certificaciones: activeDoc.certificaciones || [],
          breakdown_rating: activeDoc.breakdown_rating,
          nivel_calificacion: activeDoc.nivel_calificacion,
          direccion: activeDoc.direccion,
          foto: activeDoc.foto,
          redes: activeDoc.redes || {},
          activo: activeDoc.activo
        }),
        ...(rol === 'AFILIADO' && {
          años_experiencia: activeDoc.años_experiencia,
          biografia: activeDoc.biografia,
          calificacion: activeDoc.calificacion,
          certificaciones: activeDoc.certificaciones || [],
          foto: activeDoc.foto,
          redes: activeDoc.redes || {},
          activo: activeDoc.activo
        })
      };

      setUser(userData);

      // Sprint 8 (F2-015): ADMIN exento de sesiones_activas, pero auditar login para trazabilidad.
      if (rol === 'ADMIN') {
        try {
          const { logAudit } = await import('../utils/auditLog');
          await logAudit({
            action: 'login',
            collection: 'admin',
            docId: activeDoc.id,
            userId: activeDoc.id,
            userEmail: correoAuth,
            userRol: 'ADMIN',
            metadata: { userAgent: navigator.userAgent },
          });
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.warn('[auditLog] login:', e);
        }
      }

      // 🔐 Registrar sesión para control de sesiones simultáneas (solo no-admins)
      // Sprint 5 (F2-003): capturar token de carga; si user cambia antes de terminar, abortar setup.
      const loadToken = currentLoadRef.current;
      if (rol !== 'ADMIN') {
        const nuevoSessionId = await registrarSesion(uid, correoAuth, rol);
        if (nuevoSessionId && loadToken === currentLoadRef.current) {
          iniciarListenerSesion(uid, nuevoSessionId);
        }
      }
    } catch (e) {
      console.error('Error al obtener datos del usuario:', e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 🔐 Listener para detectar si la sesión fue cerrada desde otro dispositivo
  const iniciarListenerSesion = (uid: string, currentSessionId: string) => {
    // Limpiar listener anterior si existe
    if (sessionListenerRef.current) {
      sessionListenerRef.current();
    }

    const docRef = doc(db, 'sesiones_activas', `${uid}_${currentSessionId}`);

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();

        // Si la sesión fue marcada como inactiva desde otro dispositivo
        if (data.activa === false) {
          if (process.env.NODE_ENV === 'development') console.log('Sesión cerrada remotamente');

          // Limpiar el listener antes de mostrar el mensaje
          if (sessionListenerRef.current) {
            sessionListenerRef.current();
            sessionListenerRef.current = null;
          }

          // Mostrar mensaje y cerrar sesión
          Swal.fire({
            icon: 'warning',
            title: 'Sesión cerrada',
            text: 'Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo.',
            confirmButtonColor: '#0c2339',
            allowOutsideClick: false,
            allowEscapeKey: false
          }).then(() => {
            // Limpiar estado y cerrar sesión
            sessionStorage.removeItem('ampi_session_id');
            setSessionId(null);
            setUser(null);
            signOut(auth);
            navigate('/');
          });
        }
      }
    }, (error) => {
      console.error('Error en listener de sesión:', error);
    });

    sessionListenerRef.current = unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      // Sprint 5 (F2-003): cada cambio de auth invalida setups previos en curso.
      currentLoadRef.current += 1;
      // Limpiar listener de sesión anterior antes de un nuevo load.
      if (sessionListenerRef.current) {
        sessionListenerRef.current();
        sessionListenerRef.current = null;
      }
      loadUserData(fbUser);
    });
    return () => {
      unsubscribe();
      currentLoadRef.current += 1;
      if (sessionListenerRef.current) {
        sessionListenerRef.current();
        sessionListenerRef.current = null;
      }
    };
  }, []);

  // 🔄 Función para refrescar manualmente los datos del usuario
  const refreshUser = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      await loadUserData(currentUser);
    }
  };

  const logout = async (showMessage: boolean = true) => {
    try {
      // 🔐 Cerrar sesión en Firestore antes de hacer signOut
      const currentSessionId = sessionId || sessionStorage.getItem('ampi_session_id');
      if (user && currentSessionId && user.rol !== 'ADMIN') {
        await cerrarSesionFirestore(user.id, currentSessionId);
      }

      // Limpiar listener de sesión
      if (sessionListenerRef.current) {
        sessionListenerRef.current();
        sessionListenerRef.current = null;
      }

      // Limpiar sessionStorage
      sessionStorage.removeItem('ampi_session_id');
      // Sprint 9 (F6-006): limpiar rolElegido de admin_comite al cerrar sesión.
      sessionStorage.removeItem('rolElegido');
      setSessionId(null);

      await signOut(auth);
      setUser(null);

      if (showMessage) {
        toast.success('Sesión cerrada correctamente', { position: 'top-right', autoClose: 3000 });
      }
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Error al cerrar sesión');
    }
  };

  // Sprint 5 (F2-004): timer + listeners montados UNA sola vez.
  // Se leen user/logout desde refs para evitar rerun del useEffect (causaba fuga de listeners).
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    logoutRef.current = logout;
  });

  useEffect(() => {
    const INACTIVITY_MS = 2 * 60 * 60 * 1000; // 2 horas

    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        if (userRef.current) {
          toast.info('Sesión cerrada por inactividad', { position: 'top-right', autoClose: 3000 });
          logoutRef.current?.(false);
        }
      }, INACTIVITY_MS);
    };

    // Throttle mousemove: solo reinicia el timer cada 30s (no en cada pixel movido).
    let lastMoveAt = 0;
    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMoveAt < 30_000) return;
      lastMoveAt = now;
      resetTimer();
    };

    window.addEventListener('click', resetTimer);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', resetTimer);
    resetTimer();

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', resetTimer);
    };
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, logout, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};
